import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostcodeSearch } from './usePostcodeSearch';
import schoolLogo from '../school-brand.jpg';
import * as Styles from './styles';

function Register() {
    const navigate = useNavigate();

    // Submission Success State
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Accordion Step State ('step1', 'step2', 'step3')
    const [activeStep, setActiveStep] = useState('step1');

    // Instructor Details
    const [title, setTitle] = useState('Mr');
    const [firstName, setFirstName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [postcode, setPostcode] = useState('');

    // Coverage & Instructor Details
    const [travelRadius, setTravelRadius] = useState(5);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);

    // Terms & Conditions Checkbox
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const {
        nearbyAreas,
        findNearbyAreas,
        loading,
        isValid
    } = usePostcodeSearch();

    const availableCategories = [
        "Car (Manual)",
        "Car (Automatic)",
        "Motorbike License"
    ];

    useEffect(() => {
        if (postcode && postcode.replace(/\s/g, '').length >= 3) {
            const timer = setTimeout(() => {
                findNearbyAreas(postcode, travelRadius);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [postcode, travelRadius, findNearbyAreas]);

    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(item => item !== category)
                : [...prev, category]
        );
    };

    const handleAreaToggle = (area) => {
        setSelectedAreas(prev =>
            prev.includes(area)
                ? prev.filter(item => item !== area)
                : [...prev, area]
        );
    };

    const handleApply = async (e) => {
        e.preventDefault();

        if (!firstName || !surname || !email || !phone) {
            alert("Please fill in your name, email, and telephone number.");
            setActiveStep('step1');
            return;
        }

        if (postcode && isValid === false) {
            alert("Please enter a valid postcode or leave it blank.");
            return;
        }

        if (!agreedToTerms) {
            alert("You must agree to the Terms & Conditions before registering.");
            return;
        }

        try {
            const payload = new FormData();

            payload.append(
                'name',
                `${title} ${firstName} ${surname}`
            );

            payload.append('email', email);
            payload.append('phone', phone);
            payload.append('address', address || '');
            payload.append('postcode', postcode || '');
            payload.append('travel_radius_miles', travelRadius);
            payload.append(
                'transmission_type',
                JSON.stringify(selectedCategories.length > 0 ? selectedCategories : ["Car (Manual)"])
            );
            payload.append(
                'selected_areas',
                JSON.stringify(selectedAreas)
            );

            const response = await fetch('/api/instructors', {
                method: 'POST',
                body: payload
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok) {
                setIsSubmitted(true);
            } else {
                alert(`❌ Failed: ${data.error || "Unknown error."}`);
            }

        } catch (err) {
            console.error(err);
            alert("❌ Network error. Please try again.");
        }
    };

    // ============================================================
    // SUCCESS SCREEN
    // ============================================================

    if (isSubmitted) {
        return (
            <div
                style={{
                    ...Styles.containerStyle,
                    minHeight: '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '40px 20px'
                }}
            >
                <div
                    style={{
                        background: '#ffffff',
                        padding: '50px 40px',
                        borderRadius: '20px',
                        maxWidth: '550px',
                        width: '100%',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                        textAlign: 'center'
                    }}
                >
                    <div
                        style={{
                            width: '64px',
                            height: '64px',
                            margin: '0 auto 20px',
                            borderRadius: '50%',
                            background: '#dcfce7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px'
                        }}
                    >
                        ✓
                    </div>

                    <h2
                        style={{
                            color: '#111827',
                            fontSize: '28px',
                            fontWeight: '800',
                            marginBottom: '12px',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Registration Received
                    </h2>

                    <p
                        style={{
                            color: '#64748b',
                            fontSize: '15px',
                            lineHeight: '1.7',
                            marginBottom: '28px'
                        }}
                    >
                        Thank you for registering with Lesson-Lane.
                        Your instructor account request has been received.
                        We'll let you know when your account is ready.
                    </p>

                    <button
                        onClick={() => navigate('/')}
                        style={primaryButtonStyle}
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    // ============================================================
    // REGISTRATION PAGE (SMOOTH WIZARD)
    // ============================================================

    return (
        <div
            style={{
                ...Styles.containerStyle,
                padding: '40px 15px',
                minHeight: '80vh',
                boxSizing: 'border-box',
                width: '100%',
                overflowX: 'hidden'
            }}
        >
            <div
                style={{
                    maxWidth: '850px',
                    margin: '0 auto',
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >

                {/* PAGE HEADER WITH LOGO WATERMARK */}

                <div
                    style={{
                        textAlign: 'center',
                        marginBottom: '30px',
                        padding: '20px 10px',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {/* Subtle Watermark Logo Background */}
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '220px', height: '220px', backgroundImage: `url(${schoolLogo})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', opacity: 0.10, pointerEvents: 'none', zIndex: 0 }} />

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div
                            style={{
                                color: '#2563eb',
                                fontSize: '0.85rem',
                                fontWeight: '800',
                                letterSpacing: '1px',
                                marginBottom: '10px'
                            }}
                        >
                            INSTRUCTOR REGISTRATION
                        </div>

                        <h1
                            style={{
                                fontSize: 'clamp(1.8rem, 5vw, 3.2rem)',
                                fontWeight: '800',
                                margin: '0 0 15px',
                                letterSpacing: '-1px',
                                color: '#111827'
                            }}
                        >
                            Create Your Instructor Account
                        </h1>

                        <p
                            style={{
                                color: '#64748b',
                                fontSize: '1rem',
                                lineHeight: '1.6',
                                maxWidth: '650px',
                                margin: '0 auto'
                            }}
                        >
                            Join Lesson-Lane and manage your own students,
                            lessons, calendar, bookings, messaging and progress
                            all in one place.
                        </p>
                    </div>
                </div>

                {/* FORM CARD WITH SMOOTH ACCORDION SECTIONS */}

                <div
                    style={{
                        background: '#ffffff',
                        paddingTop: '30px',
                        paddingBottom: '30px',
                        paddingLeft: 'clamp(15px, 5vw, 40px)',
                        paddingRight: 'clamp(15px, 5vw, 40px)',
                        borderRadius: '20px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
                        boxSizing: 'border-box',
                        width: '100%'
                    }}
                >
                    <form
                        onSubmit={handleApply}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            width: '100%'
                        }}
                    >

                        {/* ================= SECTION 1: PERSONAL DETAILS ================= */}
                        <div style={accordionSectionStyle(activeStep === 'step1')}>
                            <div 
                                onClick={() => setActiveStep('step1')}
                                style={accordionHeaderStyle}
                            >
                                <div>
                                    <h2 style={sectionHeadingStyle}>Personal Details</h2>
                                    <p style={sectionDescriptionStyle}>Tell us a little about yourself.</p>
                                </div>
                                <span style={{ fontSize: '16px', color: '#2563eb', fontWeight: 'bold' }}>
                                    {activeStep === 'step1' ? '▲' : '▼'}
                                </span>
                            </div>

                            {activeStep === 'step1' && (
                                <div style={accordionBodyStyle}>
                             
{/* Responsive Name Row: Stacks vertically on mobile, side-by-side on desktop */}
<div
    style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '15px',
        marginBottom: '20px'
    }}
>
    <div style={{ minWidth: '90px', flex: '1' }}>
        <label style={labelStyle}>Title</label>
        <select
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={inputStyle}
        >
            <option>Mr</option>
            <option>Mrs</option>
            <option>Ms</option>
            <option>Dr</option>
        </select>
    </div>

    <div style={{ flex: '2' }}>
        <label style={labelStyle}>First Name</label>
        <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
            style={inputStyle}
        />
    </div>

    <div style={{ flex: '2' }}>
        <label style={labelStyle}>Surname</label>
        <input
            type="text"
            placeholder="Surname"
            value={surname}
            onChange={e => setSurname(e.target.value)}
            required
            style={inputStyle}
        />
    </div>
</div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                            gap: '15px',
                                            marginBottom: '25px'
                                        }}
                                    >
                                        <div>
                                            <label style={labelStyle}>Email Address</label>
                                            <input
                                                type="email"
                                                placeholder="you@example.com"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        <div>
                                            <label style={labelStyle}>Telephone</label>
                                            <input
                                                type="tel"
                                                placeholder="Your phone number"
                                                value={phone}
                                                onChange={e => setPhone(e.target.value)}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveStep('step2')}
                                            style={secondaryButtonStyle}
                                        >
                                            Next Step ➔
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ================= SECTION 2: TEACHING AREA ================= */}
                        <div style={accordionSectionStyle(activeStep === 'step2')}>
                            <div 
                                onClick={() => setActiveStep('step2')}
                                style={accordionHeaderStyle}
                            >
                                <div>
                                    <h2 style={sectionHeadingStyle}>Your Teaching Area</h2>
                                    <p style={sectionDescriptionStyle}>Set your main location and the area you are happy to cover.</p>
                                </div>
                                <span style={{ fontSize: '16px', color: '#2563eb', fontWeight: 'bold' }}>
                                    {activeStep === 'step2' ? '▲' : '▼'}
                                </span>
                            </div>

                            {activeStep === 'step2' && (
                                <div style={accordionBodyStyle}>
                                    {/* PROMOTIONAL INCENTIVE BANNER */}
                                    <div style={{
                                        background: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '10px',
                                        padding: '14px 16px',
                                        marginBottom: '20px',
                                        color: '#166534',
                                        fontSize: '13px',
                                        lineHeight: '1.5'
                                    }}>
                                        💡 <strong>Tip:</strong> Fill this out so we can promote your profile to new local learners looking for instructors in your area! <em>(Optional — you can also add this later in your profile settings).</em>
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={labelStyle}>Home Address</label>
                                        <input
                                            type="text"
                                            placeholder="Your home or business address"
                                            value={address}
                                            onChange={e => setAddress(e.target.value)}
                                            style={inputStyle}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={labelStyle}>Postcode</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. NG18 1AA"
                                            value={postcode}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setPostcode(val);
                                                if (val.length > 2) {
                                                    findNearbyAreas(val, travelRadius);
                                                }
                                            }}
                                            style={{
                                                ...inputStyle,
                                                textTransform: 'uppercase'
                                            }}
                                        />
                                        <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: '600' }}>
                                            {loading && <span style={{ color: '#64748b' }}>Checking postcode...</span>}
                                            {isValid === true && <span style={{ color: '#16a34a' }}>✓ Valid postcode</span>}
                                            {isValid === false && <span style={{ color: '#dc2626' }}>⚠ Postcode not recognised</span>}
                                        </div>
                                    </div>

                                    {/* TRAVEL RADIUS */}
                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            padding: '20px',
                                            marginBottom: '20px'
                                        }}
                                    >
                                        <label style={{ ...labelStyle, marginBottom: '12px' }}>
                                            Travel Radius
                                        </label>

                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '10px'
                                            }}
                                        >
                                            <span style={{ color: '#64748b', fontSize: '13px' }}>
                                                How far are you willing to travel?
                                            </span>

                                            <strong style={{ color: '#2563eb', fontSize: '15px' }}>
                                                {travelRadius} miles
                                            </strong>
                                        </div>

                                        <input
                                            type="range"
                                            min="1"
                                            max="10"
                                            value={travelRadius}
                                            onChange={e => {
                                                const newRadius = e.target.value;
                                                setTravelRadius(newRadius);
                                                if (postcode.length > 2) {
                                                    findNearbyAreas(postcode, newRadius);
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                accentColor: '#2563eb',
                                                cursor: 'pointer'
                                            }}
                                        />

                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                color: '#94a3b8',
                                                fontSize: '11px',
                                                marginTop: '5px'
                                            }}
                                        >
                                            <span>1 mile</span>
                                            <span>10 miles</span>
                                        </div>
                                    </div>

                                    {/* COVERAGE AREAS */}
                                    <div style={{ marginBottom: '25px' }}>
                                        <label style={labelStyle}>Coverage Areas</label>
                                        <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 10px' }}>
                                            Select the areas you are happy to teach in.
                                        </p>

                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                                minHeight: '40px'
                                            }}
                                        >
                                            {nearbyAreas.length > 0 ? (
                                                nearbyAreas.map(area => (
                                                    <button
                                                        type="button"
                                                        key={area}
                                                        onClick={() => handleAreaToggle(area)}
                                                        style={buttonStyle(selectedAreas.includes(area))}
                                                    >
                                                        {selectedAreas.includes(area) ? '✓ ' : ''}
                                                        {area}
                                                    </button>
                                                ))
                                            ) : (
                                                <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                                                    Enter your postcode above to see nearby areas.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveStep('step3')}
                                            style={secondaryButtonStyle}
                                        >
                                            Next Step ➔
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ================= SECTION 3: VEHICLE TYPES ================= */}
                        <div style={accordionSectionStyle(activeStep === 'step3')}>
                            <div 
                                onClick={() => setActiveStep('step3')}
                                style={accordionHeaderStyle}
                            >
                                <div>
                                    <h2 style={sectionHeadingStyle}>Vehicle Types</h2>
                                    <p style={sectionDescriptionStyle}>Select the types of training you provide.</p>
                                </div>
                                <span style={{ fontSize: '16px', color: '#2563eb', fontWeight: 'bold' }}>
                                    {activeStep === 'step3' ? '▲' : '▼'}
                                </span>
                            </div>

                            {activeStep === 'step3' && (
                                <div style={accordionBodyStyle}>
                                    {/* PROMOTIONAL INCENTIVE BANNER */}
                                    <div style={{
                                        background: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '10px',
                                        padding: '14px 16px',
                                        marginBottom: '20px',
                                        color: '#166534',
                                        fontSize: '13px',
                                        lineHeight: '1.5'
                                    }}>
                                        💡 <strong>Tip:</strong> Select your training categories so learners looking for specific transmissions can find your profile faster!
                                    </div>

                                    <div style={{ marginBottom: '25px' }}>
                                        <label style={labelStyle}>Vehicle Types</label>
                                        <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 10px' }}>
                                            Select the types of training you provide.
                                        </p>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            {availableCategories.map(category => (
                                                <button
                                                    type="button"
                                                    key={category}
                                                    onClick={() => handleCategoryToggle(category)}
                                                    style={buttonStyle(selectedCategories.includes(category))}
                                                >
                                                    {selectedCategories.includes(category) ? '✓ ' : ''}
                                                    {category}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* TERMS AND CONDITIONS TICKBOX */}
                                    <div 
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            gap: '12px',
                                            background: '#f8fafc',
                                            padding: '15px',
                                            borderRadius: '10px',
                                            border: '1px solid #e2e8f0',
                                            marginBottom: '20px'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            id="terms"
                                            checked={agreedToTerms}
                                            onChange={e => setAgreedToTerms(e.target.checked)}
                                            style={{ marginTop: '3px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                                            required
                                        />
                                        <label htmlFor="terms" style={{ fontSize: '13px', color: '#334155', cursor: 'pointer', lineHeight: '1.5' }}>
                                            I agree to the <span style={{ color: '#2563eb', fontWeight: '600' }}>Terms & Conditions</span> and confirm that I will maintain accurate records and use Lesson-Lane responsibly.
                                        </label>
                                    </div>

                                    {/* SUBMIT BUTTON */}
                                    <button
                                        type="submit"
                                        style={{
                                            ...primaryButtonStyle,
                                            width: '100%',
                                            marginTop: '5px'
                                        }}
                                    >
                                        Create Instructor Account
                                    </button>
                                </div>
                            )}
                        </div>

                        <p
                            style={{
                                textAlign: 'center',
                                color: '#94a3b8',
                                fontSize: '12px',
                                lineHeight: '1.5',
                                margin: '10px 0 0'
                            }}
                        >
                            By registering, you agree to use Lesson-Lane responsibly and provide accurate account information.
                        </p>
                    </form>
                </div>

                {/* BOTTOM HELP */}

                <div
                    style={{
                        textAlign: 'center',
                        marginTop: '30px',
                        color: '#64748b',
                        fontSize: '14px'
                    }}
                >
                    Already have an account?{' '}
                    <button
                        type="button"
                        onClick={() => navigate('/login')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            fontWeight: '700',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '14px'
                        }}
                    >
                        Login here
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ============================================================
   STYLES & ACCORDION HELPERS
   ============================================================ */

const accordionSectionStyle = (isOpen) => ({
    border: '1px solid',
    borderColor: isOpen ? '#2563eb' : '#e2e8f0',
    borderRadius: '14px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    background: isOpen ? '#ffffff' : '#fcfcfc'
});

const accordionHeaderStyle = {
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    background: '#ffffff'
};

const accordionBodyStyle = {
    padding: '0 20px 25px 20px',
    borderTop: '1px solid #f1f5f9'
};

const sectionHeadingStyle = {
    fontSize: '1.25rem',
    fontWeight: '800',
    color: '#111827',
    margin: '0 0 5px',
    letterSpacing: '-0.3px'
};

const sectionDescriptionStyle = {
    color: '#64748b',
    fontSize: '0.9rem',
    lineHeight: '1.5',
    margin: 0
};

const labelStyle = {
    fontSize: '0.75rem',
    color: '#334155',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '7px',
    display: 'block'
};

const inputStyle = {
    padding: '13px 14px',
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    color: '#111827',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '500',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
};

const primaryButtonStyle = {
    padding: '14px 25px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '15px',
    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.18)',
    transition: 'all 0.2s ease'
};

const secondaryButtonStyle = {
    padding: '11px 20px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '14px',
    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.18)',
    transition: 'all 0.2s ease'
};

const buttonStyle = (isSelected) => ({
    padding: '9px 14px',
    background: isSelected ? '#2563eb' : '#f8fafc',
    color: isSelected ? '#ffffff' : '#334155',
    border: isSelected
        ? '1px solid #2563eb'
        : '1px solid #cbd5e1',
    borderRadius: '9px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s ease'
});

export default Register;